import { combineReducers, configureStore, Reducer, UnknownAction } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/es/storage';
import { crossSellApi } from 'store/crossSell/crossSellApi';

import aiAssistantReducer from './aiAssistant/aiAssistantApi';
import { apiSlice } from './api/apiSlice';
import avsSelectionReducer from './avs/avsSelectionSlice';
import { basicTableSlice } from './basicTable/basicTableApi';
import './bridge/bridgeSlice'; // side-effect import — injects bridge endpoints into apiSlice
import bulkEditReducer from './bulkEdit/bulkEditSlice';
import calendarReducer from './calendar/calendarSlice';
import channelsReducer from './channels/channelsSlice';
import chatReducer from './chat/chatSlice';
import customerFeedbackReducer from './customerFeedback/customerFeedbackSlice';
import { rtkQueryErrorLogger } from './errorCatchingMiddleware/errorCatchingMiddleware';
import labOrdersReducer from './labOrders/labOrdersSlice';
import modalReducer from './modal/modalSlice';
import notificationsReducer from './notifications/notificationsSlice';
import orderNotesReducer from './orderNotes/orderNotesSlice';
import orderPaneReducer from './orderPane/orderPaneSlice';
import { ordersApi } from './orders/ordersApi';
import ordersReducer from './orders/ordersSlice';
import patientReducer from './patients/patientsSlice';
import patientSelectionReducer from './patientSelection/patientSelectionSlice';
import { userPersistTransform } from './persistence/userPersistTransform';
import prescriptionPaneReducer from './prescriptionPane/prescriptionPaneSlice';
import prescriptionsReducer from './prescriptions/prescriptionsSlice';
import { qaCacheMiddleware } from './qa/qaCacheMiddleware';
import qaCacheReducer from './qa/qaCacheSlice';
import queueReducer from './queue/queueSlice';
import reviewsReducer from './reviews/reviewsApi';
import scheduleTemplatesReducer from './scheduling/scheduleTemplatesSlice';
import schedulingSidePanelReducer from './scheduling/schedulingSidePanelSlice';
import sharedFormContextsReducer from './sharedFormContexts/sharedFormContextsSlice';
import slidingInPaneReducer from './slidingInPane/slidingInPaneSlice';
import smsReducer from './sms/smsSlice';
import staffFeedbackReducer from './staffFeedback/staffFeedbackSlice';
import staffNotesReducer from './staffNotes/staffNotesSlice';
import systemReducer from './system/systemSlice';
import { taskSlice, tasksSlice } from './tasks/tasksSlice';
import triggersReducer from './triggers/triggersSlice';
import userReducer from './user/userSlice';
import videoCallReducer from './videoCall/videoCallSlice';

const persistConfig = {
  key: 'root',
  storage,
  transforms: [userPersistTransform],
  whitelist: ['user']
};

const middlewares = [
  apiSlice.middleware,
  crossSellApi.middleware,
  rtkQueryErrorLogger,
  ordersApi.middleware,
  qaCacheMiddleware
];

const appReducer = combineReducers({
  aiAssistant: aiAssistantReducer,
  [apiSlice.reducerPath]: apiSlice.reducer,
  avsSelection: avsSelectionReducer,
  basicTables: basicTableSlice.reducer,
  bulkEdit: bulkEditReducer,
  calendar: calendarReducer,
  channels: channelsReducer,
  chat: chatReducer,
  [crossSellApi.reducerPath]: crossSellApi.reducer,
  customerFeedback: customerFeedbackReducer,
  labOrders: labOrdersReducer,
  modal: modalReducer,
  notifications: notificationsReducer,
  orderNotes: orderNotesReducer,
  orderPane: orderPaneReducer,
  orders: ordersReducer,
  [ordersApi.reducerPath]: ordersApi.reducer,
  patient: patientReducer,
  patientSelection: patientSelectionReducer,
  prescriptionPane: prescriptionPaneReducer,
  prescriptions: prescriptionsReducer,
  qaCache: qaCacheReducer,
  queue: queueReducer,
  reviews: reviewsReducer,
  scheduleTemplates: scheduleTemplatesReducer,
  schedulingSidePanel: schedulingSidePanelReducer,
  sharedFormContexts: sharedFormContextsReducer,
  slidingInPane: slidingInPaneReducer,
  sms: smsReducer,
  staffFeedback: staffFeedbackReducer,
  staffNotes: staffNotesReducer,
  system: systemReducer,
  task: taskSlice.reducer,
  tasks: tasksSlice.reducer,
  triggers: triggersReducer,
  user: userReducer,
  videoCall: videoCallReducer
});

const rootReducer: Reducer = (state: RootState, action: UnknownAction) => appReducer(state, action);

const persistedReducer = persistReducer(persistConfig, rootReducer);

export function setupStore(preloadedState?: Partial<RootState>) {
  return configureStore({
    devTools: import.meta.env.VITE_APP_TITLE !== 'production',
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({ serializableCheck: false }).concat(middlewares),
    preloadedState,
    reducer: persistedReducer
  });
}

export const store = configureStore({
  devTools: import.meta.env.VITE_APP_TITLE !== 'production',
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }).concat(middlewares),
  reducer: persistedReducer
});

setupListeners(store.dispatch);

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof appReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore['dispatch'];
